import { useCallback, useState } from 'react';

export interface AudioCheckResult {
  name: string;
  passed: boolean;
  message: string;
}

export interface UsePreSessionAudioCheckReturn {
  checkResults: AudioCheckResult[];
  isChecking: boolean;
  runChecks: () => Promise<void>;
}

/** Keywords (lowercase) that indicate a Bluetooth audio device. */
const BLUETOOTH_KEYWORDS = ['bluetooth', 'bt', 'airpods', 'wireless'];

function labelMatchesBluetooth(label: string): boolean {
  const lower = label.toLowerCase();
  return BLUETOOTH_KEYWORDS.some((kw) => lower.includes(kw));
}

/**
 * Check 1 — Microphone access.
 * Requests getUserMedia with audio constraints and immediately releases the stream.
 * Advisory: permission refusal is noted but does not block session start.
 */
async function checkMicrophoneAccess(): Promise<AudioCheckResult> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    return {
      name: 'Microphone Access',
      passed: true,
      message: 'Microphone permission granted.',
    };
  } catch (err) {
    let message = 'Microphone access failed.';
    if (err instanceof DOMException) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        message = 'Microphone permission denied. Audio capture will not work.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        message = 'No microphone detected. Please connect a microphone.';
      } else {
        message = `Microphone error: ${err.message}`;
      }
    }
    return { name: 'Microphone Access', passed: false, message };
  }
}

/**
 * Check 2 — Audio output device.
 * Verifies that at least one audiooutput device is enumerated.
 */
async function checkAudioOutputDevice(): Promise<AudioCheckResult> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const outputDevices = devices.filter((d) => d.kind === 'audiooutput');
    if (outputDevices.length > 0) {
      return {
        name: 'Audio Output Device',
        passed: true,
        message: `Found ${outputDevices.length} audio output device(s).`,
      };
    }
    return {
      name: 'Audio Output Device',
      passed: false,
      message: 'No audio output devices detected. Ensure speakers or a headset are connected.',
    };
  } catch {
    return {
      name: 'Audio Output Device',
      passed: false,
      message: 'Could not enumerate audio devices.',
    };
  }
}

/**
 * Check 3 — Bluetooth headset.
 * Looks for a Bluetooth audio output device via enumerateDevices().
 * Note: device labels may be empty without prior microphone permission.
 */
async function checkBluetoothHeadset(): Promise<AudioCheckResult> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const bluetoothDevice = devices.find(
      (d) => d.kind === 'audiooutput' && labelMatchesBluetooth(d.label),
    );

    if (bluetoothDevice) {
      return {
        name: 'Bluetooth Headset',
        passed: true,
        message: `Bluetooth headset detected: ${bluetoothDevice.label || 'unnamed device'}.`,
      };
    }

    // If there are output devices but none match Bluetooth keywords, device labels
    // may not be available (requires prior getUserMedia permission). Provide an
    // informative message rather than a hard failure.
    const outputDevices = devices.filter((d) => d.kind === 'audiooutput');
    if (outputDevices.length > 0 && outputDevices.every((d) => d.label === '')) {
      return {
        name: 'Bluetooth Headset',
        passed: false,
        message:
          'Could not identify headset type — device labels require microphone permission. ' +
          'Grant mic access and re-run checks, or verify headset is connected.',
      };
    }

    return {
      name: 'Bluetooth Headset',
      passed: false,
      message:
        'No Bluetooth headset detected. Consider using a Bluetooth headset to reduce echo and ambient noise.',
    };
  } catch {
    return {
      name: 'Bluetooth Headset',
      passed: false,
      message: 'Could not enumerate audio devices to detect Bluetooth headset.',
    };
  }
}

/**
 * usePreSessionAudioCheck — advisory pre-session audio checklist.
 *
 * Runs three checks sequentially (to avoid concurrent getUserMedia + enumerate
 * races on some browsers) and exposes results as { name, passed, message } items.
 * All checks are informational — the session can proceed regardless of results.
 *
 * Exposes: { checkResults, isChecking, runChecks }
 */
export function usePreSessionAudioCheck(): UsePreSessionAudioCheckReturn {
  const [checkResults, setCheckResults] = useState<AudioCheckResult[]>([]);
  const [isChecking, setIsChecking] = useState(false);

  const runChecks = useCallback(async () => {
    if (isChecking) return;
    setIsChecking(true);
    setCheckResults([]);

    try {
      // Run checks sequentially to avoid device enumeration race conditions.
      const micResult = await checkMicrophoneAccess();
      setCheckResults([micResult]);

      const outputResult = await checkAudioOutputDevice();
      setCheckResults([micResult, outputResult]);

      const bluetoothResult = await checkBluetoothHeadset();
      setCheckResults([micResult, outputResult, bluetoothResult]);
    } finally {
      setIsChecking(false);
    }
  }, [isChecking]);

  return { checkResults, isChecking, runChecks };
}
